import TileLayer from "ol/layer/Tile"
import TileWMS from "ol/source/TileWMS"
import { Vector as VectorLayer } from "ol/layer"
import { Vector as VectorSource } from "ol/source"
import GeoJSON from "ol/format/GeoJSON"
import { bbox as bboxStrategy } from "ol/loadingstrategy"
import WMTSCapabilities from "ol/format/WMTSCapabilities"
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS"
import type OlMap from "ol/Map"
import type { Coordinate } from "ol/coordinate"
import type BaseLayer from "ol/layer/Base"
import type { MapBrowserEvent } from "ol"

export enum LayerType {
  WMS = "wms",
  WMTS = "wmts",
  WFS = "wfs",
}

export enum WfsLayerSource {
  GIS = "gis",
  PDOK = "pdok",
}

export const useFetchedLayers = (map: Ref<OlMap | null>, organizationSettings: Ref<OrganizationSettings | null>, zoomCutoff: number) => {
  const authStore = useAuthStore()
  const { currentOrganization } = storeToRefs(authStore)

  const { getLayer, removeLayer, getAllLayers } = useLayerManagement(map)

  const pdokMapFeatures = ref<Array<MapFeatureData>>([]) // Feature info for PDOK layers - updated on click
  const gisMapFeatures = ref<Array<MapFeatureData>>([]) // Feature info for GIS layers - updated on load

  const layersDuopp = useLocalStorage<Array<string>>(`ol-${currentOrganization.value}:layer-duopp`, [])
  const layersPdok = useLocalStorage<Array<string>>("ol:layer-pdok", [])
  const layersGis = useLocalStorage<Array<string>>(`ol-${currentOrganization.value}:layer-gis`, [])

  const getGisLayers = (layerType: string): Array<BaseLayer> => {
    const allLayers = getAllLayers()!

    return allLayers.filter((layer) => layer.get("name").startsWith(`${OpenLayersLayer.GIS}:${layerType}`))
  }

  const addWmsLayer = (url: string, layerName: string, options?: TileLayer) => {
    if (!map.value) return

    // https://dservices3.arcgis.com/vIFGkPPowfxZWvaV/arcgis/services/Riool_Leiding_Openbaar/WFSServer?service=wfs&request=getcapabilities
    // https://openlayers.org/en/latest/examples/wms-tiled.html
    const layer = new TileLayer({
      ...options,
      source: new TileWMS({
        url,
        params: {
          LAYERS: layerName,
        },
        serverType: "geoserver",
        wrapX: false,
      }),
    })

    layer.set("name", layerName)

    map.value.getLayers().insertAt(1, layer)
  }

  type WfsLayerOptions = {
    version?: string // default 1.1.0
    outputFormat?: string // default application/json
    startIndex?: number // default 0
  }
  const addWfsLayer = (
    source: WfsLayerSource,
    url: string,
    layerName: string,
    typeName = layerName,
    wfsOptions?: WfsLayerOptions,
    layerOptions?: VectorLayer,
  ) => {
    if (!map.value) return

    const wfsParams = new URLSearchParams({
      service: "WFS",
      request: "GetFeature",
      srsName: "EPSG:3857",
      typeName,
      version: wfsOptions?.version ?? "1.1.0",
      outputFormat: wfsOptions?.outputFormat ?? "application/json",
      startIndex: String(wfsOptions?.startIndex ?? 0),
    })

    // https://openlayers.org/en/latest/examples/vector-wfs.html
    const wfsSource = new VectorSource({
      format: new GeoJSON(),
      // &maxFeatures=1000 <- can add this to minimize the amount of features loaded
      url: (extent) => {
        return `${url}?${wfsParams}&bbox=${extent.join(",")},EPSG:3857`
      },
      // Optionally set a strategy to load features as needed (e.g., within the current view's extent)
      strategy: bboxStrategy,
    })

    const layer = new VectorLayer({
      ...layerOptions,
      source: wfsSource,
      minZoom: zoomCutoff,
    })

    // Listen for when features are loaded to add metadata
    wfsSource.on("featuresloadend", (event) => {
      const features = event.features
      if (features) {
        features.forEach((feature) => {
          const allMetadata = feature.getProperties()
          const { geometry, ...metadata } = allMetadata

          feature.set("metadata", metadata)

          feature.set("source", source)
          feature.set("typeName", typeName)
        })
      }
    })

    layer.set("name", layerName)

    map.value.getLayers().insertAt(1, layer)
  }

  /**
   * Adds a WMTS layer to the map from a PDOK service.
   * @param url Currently only PDOK domains are supported
   * @param layerName Name of the PDOK layer
   * @returns
   */
  const addWmtsLayer = async (url: string, layerName: string) => {
    if (!map.value) return
    const parser = new WMTSCapabilities()

    try {
      // https://openlayers.org/en/latest/examples/wmts-layer-from-capabilities.html

      if (!urlContainsHost(url, ["pdok.nl"])) {
        throw new Error(`WMTS URL: '${url}' must be a valid PDOK domain.`)
      }

      // URL has been validated
      // nosemgrep
      const response = await fetch(url + "?&request=GetCapabilities&service=wmts", { cache: "force-cache" })
      const text = await response.text()
      const result = parser.read(text)

      const options = optionsFromCapabilities(result, {
        layer: layerName,
        matrixSet: "EPSG:3857",
      })

      const layer = new TileLayer({
        source: new WMTS(options!),
        opacity: 0.7,
      })

      layer.set("name", layerName)

      map.value.getLayers().insertAt(1, layer)
    } catch (error) {
      console.error("Error parsing WMTS capabilities", error)
    }
  }

  const fetchPdokLayerInfo = async (coordinate: Coordinate) => {
    const layers = layersPdok.value

    if (!map.value || !layers.length) return

    const view = map.value.getView()
    const resolution = view.getResolution()

    if (!resolution) return

    const projection = view.getProjection()

    const featuresInfos = []

    // It's possible that multiple layers are active
    // Fetch feature info for each layer
    for (const layer of layers) {
      const pdokLayer = getLayer(layer) as TileLayer
      if (!pdokLayer) return

      const source = pdokLayer.getSource() as TileWMS

      const url = source.getFeatureInfoUrl(coordinate, resolution * 2, projection, {
        INFO_FORMAT: "application/json",
      })

      if (url) {
        // trusted URL
        // nosemgrep
        const response = await fetch(url, { cache: "force-cache" })
        const json = await response.json()

        if (json.features.length) {
          featuresInfos.push({
            ...json,
            layerKey: layer,
          })
        }
      }
    }

    pdokMapFeatures.value = featuresInfos
  }
  const fetchGisLayerInfo = async (event: MapBrowserEvent) => {
    const layers = layersGis.value

    if (!map.value || !layers.length) return

    const view = map.value.getView()
    const resolution = view.getResolution()

    if (!resolution) return

    const featuresInfos: Array<MapFeatureData> = []

    map.value.forEachFeatureAtPixel(event.pixel, (feature) => {
      if (feature.get("source") !== WfsLayerSource.GIS) {
        return
      }

      const metadata = feature.get("metadata") ?? {}
      const typeName = feature.get("typeName")

      const existingLayer = featuresInfos.find((layer) => layer.layerKey === typeName)

      if (existingLayer) {
        existingLayer.features.push(metadata)
        return
      }

      featuresInfos.push({
        layerKey: typeName,
        features: [metadata],
      })
    })

    gisMapFeatures.value = featuresInfos
  }

  const togglePdokLayer = (authority: string, resource: string, layerName: string, type = LayerType.WMS) => {
    // Only load PDOK layers when PDOK is enabled for the organization
    // OR when the layer is the satellite layer
    if (!organizationSettings.value?.geoLayers && layerName !== OpenLayersLayer.SATELLITE) return

    if (removeLayer(layerName)) {
      return
    }

    // When changing the URL: Also update the fetch methods where this is used.
    // Reevaluate this if the URL changes.
    const url = `https://service.pdok.nl/${authority}/${resource}/${type}/v1_0`

    switch (type) {
      case LayerType.WMS:
        addWmsLayer(url, layerName)
        break
      case LayerType.WMTS:
        addWmtsLayer(url, layerName)
        break
      case LayerType.WFS:
        addWfsLayer(WfsLayerSource.PDOK, url, layerName)
        break
    }
  }

  const toggleDuoppLayer = async (layerKey: string) => {
    if (!organizationSettings.value?.geoLayersDuoPP) return

    if (removeLayer(layerKey)) {
      return
    }

    const url = `${duoppBaseUrl}/${organizationSettings.value?.geoLayersDuoPPOrganizationName}`

    addWmsLayer(url, layerKey)
  }

  const toggleFilterLayers = (
    newLayers: Array<string>,
    oldLayers: Array<string>,
    layerType: OpenLayersLayer.PDOK | OpenLayersLayer.DUOPP | OpenLayersLayer.GIS,
  ) => {
    if (!map.value) return

    if (newLayers.length > oldLayers.length) {
      // Add new layers
      newLayers.forEach((layer) => {
        if (!oldLayers.includes(layer)) {
          if (layerType === OpenLayersLayer.PDOK) {
            togglePdokLayer("rioned", "beheerstedelijkwater", layer)
          } else if (layerType === OpenLayersLayer.GIS) {
            toggleGisLayer(layer)
          } else {
            toggleDuoppLayer(layer)
          }
        }
      })
    } else {
      // Remove layer(s)
      oldLayers.forEach((layer) => {
        if (!newLayers.includes(layer)) removeLayer(layer)
      })
    }
  }

  const toggleGisLayer = async (layerType: string) => {
    const gisLayerBaseUrl = `${organizationSettings.value?.geoLayersGISOrganizationUrl}`

    const gisLayers = getGisLayers(layerType)

    gisLayers.forEach((layer) => removeLayer(layer.get("name")))

    const params = new URLSearchParams({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      typeName: layerType,
      srsName: "EPSG:3857",
      resultType: "hits",
    })

    type HistResult = {
      "wfs:FeatureCollection"?: {
        "@_numberMatched"?: string
      }
    }

    const hitsResult = await parseXMLFromURL<HistResult>(`${gisLayerBaseUrl}?${params}`, { ignoreAttributes: false })

    const numberMatched = Number(hitsResult["wfs:FeatureCollection"]?.["@_numberMatched"])

    if (isNaN(numberMatched)) {
      throw new Error("Invalid numberMatched value")
    }

    const totalPages = Math.ceil(numberMatched / 3000)

    for (let index = 0; index < totalPages; index++) {
      addWfsLayer(WfsLayerSource.GIS, gisLayerBaseUrl, `${OpenLayersLayer.GIS}:${layerType}:${index}`, layerType, {
        version: "2.0.0",
        outputFormat: "GEOJSON",
        startIndex: 3000 * index,
      })
    }
  }

  const initializeFetchedLayers = () => {
    if (!map.value) throw new Error("Map is not initialized")

    layersPdok.value.forEach((layer) => togglePdokLayer("rioned", "beheerstedelijkwater", layer))
    layersDuopp.value.forEach((layer) => toggleDuoppLayer(layer))
    layersGis.value.forEach((layer) => toggleGisLayer(layer))

    map.value.on("click", (event) => {
      // Fetch PDOK layer info when clicking on the map
      if (organizationSettings.value?.geoLayers && layersPdok.value.length) {
        fetchPdokLayerInfo(event.coordinate)
      }
      // Fetch PDOK layer info when clicking on the map
      if (organizationSettings.value?.geoLayersGIS && layersGis.value.length) {
        fetchGisLayerInfo(event)
      }
    })
  }

  watch(layersDuopp, (newValue, oldValue) => toggleFilterLayers(newValue, oldValue, OpenLayersLayer.DUOPP))
  watch(layersPdok, (newValue, oldValue) => toggleFilterLayers(newValue, oldValue, OpenLayersLayer.PDOK))
  watch(layersGis, (newValue, oldValue) => toggleFilterLayers(newValue, oldValue, OpenLayersLayer.GIS))

  return {
    togglePdokLayer,
    pdokMapFeatures,
    gisMapFeatures,
    initializeFetchedLayers,
  }
}
